// ── Account Security Page (#/account/security) ──
//
// Sign-in method summary, TOTP 2FA enrollment/disable, backup-code recovery,
// and set/change password. Account-level (not project-scoped), unlike
// pages/settings.js — mirrors its page-module shape though.

import qrcode from 'qrcode-generator';
import {
  checkAuth, getUser, verifySession,
  verifyTwoFactorSetup, disableTwoFactor, regenerateBackupCodes, setAccountPassword,
} from '../auth.js';
import { api } from '../api.js';
import { showToast, esc } from '../ui.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';

// 2FA setup is two calls: /2fa/setup (mints a secret, not yet enabled) then
// /2fa/verify (first code confirms it). Hold the in-progress otpauth_url/secret
// here across the re-render between those two calls.
let setupData = null; // { secret, otpauth_url } | null

// Backup codes are handed back in plaintext exactly once per call (initial
// enrollment or a regenerate) — held here just long enough for the user to
// see/copy them, cleared once they acknowledge.
let oneTimeCodes = null; // string[] | null

let disableFormOpen = false;
let regenerateFormOpen = false;
let passwordFormOpen = false;

function renderQrSvg(otpauthUrl) {
  const qr = qrcode(0, 'M');
  qr.addData(otpauthUrl);
  qr.make();
  return qr.createSvgTag({ cellSize: 5, margin: 4 });
}

function renderSignInMethods(user) {
  return `
  <div class="settings-section">
    <div class="card">
      <h3>Sign-in Methods</h3>
      <table class="members-table">
        <tbody>
          <tr><td>Google</td><td>${user.google_id ? '<span class="badge badge-completed">Connected</span>' : '<span class="text-muted">—</span>'}</td></tr>
          <tr><td>Password</td><td>${user.has_password ? '<span class="badge badge-completed">Set</span>' : '<span class="text-muted">Not set</span>'}</td></tr>
        </tbody>
      </table>
      ${passwordFormOpen ? renderPasswordForm(user) : `<button class="btn btn-ghost mt-2" id="open-password-btn">${user.has_password ? 'Change password' : 'Set password'}</button>`}
    </div>
  </div>`;
}

function renderPasswordForm(user) {
  return `
  <form id="password-form" class="mt-2" style="max-width:320px;">
    ${user.has_password ? `
    <div class="form-group">
      <label class="form-label" for="pw-current">Current password</label>
      <input class="form-input" id="pw-current" type="password" autocomplete="current-password" required>
    </div>` : ''}
    <div class="form-group">
      <label class="form-label" for="pw-new">${user.has_password ? 'New password' : 'Password'}</label>
      <input class="form-input" id="pw-new" type="password" autocomplete="new-password" minlength="8" required>
    </div>
    ${user.totp_enabled ? `
    <div class="form-group">
      <label class="form-label" for="pw-code">Authenticator code</label>
      <input class="form-input" id="pw-code" autocomplete="one-time-code" required>
    </div>` : ''}
    <div class="login-error" id="password-error"></div>
    <div class="flex gap-1">
      <button type="submit" class="btn btn-primary" id="password-submit">${user.has_password ? 'Change password' : 'Set password'}</button>
      <button type="button" class="btn btn-ghost" id="password-cancel">Cancel</button>
    </div>
  </form>`;
}

function renderBackupCodesPane(codes) {
  return `
  <div class="settings-section">
    <div class="card">
      <h3>Two-Factor Authentication</h3>
      <p><strong>Save these backup codes now</strong> — each works once, in place of an authenticator code, and this is the only time they're shown.</p>
      <div class="backup-codes">${codes.map(c => `<span>${esc(c)}</span>`).join('')}</div>
      <div class="flex gap-1 mt-2">
        <button class="btn btn-ghost" id="copy-codes-btn">Copy codes</button>
        <button class="btn btn-primary" id="ack-codes-btn">I've saved these</button>
      </div>
    </div>
  </div>`;
}

function renderTwofaEnabled(user) {
  return `
  <div class="settings-section">
    <div class="card">
      <h3>Two-Factor Authentication</h3>
      <p><span class="badge badge-completed">Enabled</span></p>
      <p class="text-muted mt-2">${user.backup_codes_remaining} of 10 backup codes remaining.</p>
      <div class="flex gap-1 mt-2">
        <button class="btn btn-ghost" id="open-regenerate-btn">Regenerate backup codes</button>
        <button class="btn btn-danger" id="open-disable-btn">Disable 2FA</button>
      </div>
      ${regenerateFormOpen ? renderConfirmForm('regenerate', 'Regenerate codes', false) : ''}
      ${disableFormOpen ? renderConfirmForm('disable', 'Disable 2FA', true) : ''}
    </div>
  </div>`;
}

// Shared confirm form for disable / regenerate — password or a code
// (regenerate: authenticator code only; disable: authenticator or backup
// code, since a lost device is exactly the case disable exists for).
function renderConfirmForm(prefix, submitLabel, allowBackupCode) {
  return `
  <form id="${prefix}-form" class="mt-2" style="max-width:320px;">
    <p class="text-muted">Confirm with your password or a${allowBackupCode ? 'n authenticator or backup' : 'n authenticator'} code.</p>
    <div class="form-group">
      <label class="form-label" for="${prefix}-password">Password</label>
      <input class="form-input" id="${prefix}-password" type="password" autocomplete="current-password" placeholder="Leave blank to use a code">
    </div>
    <div class="form-group">
      <label class="form-label" for="${prefix}-code">${allowBackupCode ? 'Authenticator or backup code' : 'Authenticator code'}</label>
      <input class="form-input" id="${prefix}-code" autocomplete="one-time-code" placeholder="Leave blank to use password">
    </div>
    <div class="login-error" id="${prefix}-error"></div>
    <div class="flex gap-1">
      <button type="submit" class="btn ${prefix === 'disable' ? 'btn-danger' : 'btn-primary'}" id="${prefix}-submit">${submitLabel}</button>
      <button type="button" class="btn btn-ghost" id="${prefix}-cancel">Cancel</button>
    </div>
  </form>`;
}

function renderTwofaPrompt() {
  return `
  <div class="settings-section">
    <div class="card">
      <h3>Two-Factor Authentication</h3>
      <p class="text-muted">Not enabled.</p>
      <button class="btn btn-primary mt-2" id="enable-2fa-btn">Enable 2FA</button>
    </div>
  </div>`;
}

function renderTwofaSetup(secret, otpauthUrl) {
  return `
  <div class="settings-section">
    <div class="card">
      <h3>Two-Factor Authentication</h3>
      <p>Scan this QR code with Authy, Google Authenticator, or any TOTP app.</p>
      <div class="totp-qr">${renderQrSvg(otpauthUrl)}</div>
      <p class="text-muted mt-2">Or enter this key manually:</p>
      <code class="totp-secret">${esc(secret)}</code>
      <form id="verify-2fa-form" class="mt-2">
        <div class="form-group">
          <label class="form-label" for="verify-2fa-code">Enter the code from your app</label>
          <input class="form-input" id="verify-2fa-code" inputmode="numeric" pattern="[0-9]*" maxlength="6" autocomplete="one-time-code" required style="max-width:160px;">
        </div>
        <div class="login-error" id="verify-2fa-error"></div>
        <button type="submit" class="btn btn-primary" id="verify-2fa-btn">Verify &amp; Enable</button>
      </form>
    </div>
  </div>`;
}

function renderTwofaSection(user) {
  if (oneTimeCodes) return renderBackupCodesPane(oneTimeCodes);
  if (user.totp_enabled) return renderTwofaEnabled(user);
  if (setupData) return renderTwofaSetup(setupData.secret, setupData.otpauth_url);
  return renderTwofaPrompt();
}

function render(user) {
  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header">
      <h2>Security</h2>
    </div>
    ${renderSignInMethods(user)}
    ${renderTwofaSection(user)}`;
  bind(user);
}

// Reads whichever of the confirm form's password/code fields the user
// filled in — server accepts either. Empty strings become undefined so
// they're dropped from the JSON body rather than sent as ''.
function readConfirmFields(content, prefix) {
  const password = content.querySelector(`#${prefix}-password`).value;
  const code = content.querySelector(`#${prefix}-code`).value.trim();
  return { password: password || undefined, code: code || undefined };
}

function bind(user) {
  const content = getContentEl();

  // ── Password set/change ──
  const openPasswordBtn = content.querySelector('#open-password-btn');
  if (openPasswordBtn) {
    openPasswordBtn.addEventListener('click', () => { passwordFormOpen = true; render(user); });
  }
  const passwordCancel = content.querySelector('#password-cancel');
  if (passwordCancel) {
    passwordCancel.addEventListener('click', () => { passwordFormOpen = false; render(user); });
  }
  const passwordForm = content.querySelector('#password-form');
  if (passwordForm) {
    passwordForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = content.querySelector('#password-submit');
      const errEl = content.querySelector('#password-error');
      const currentEl = content.querySelector('#pw-current');
      const codeEl = content.querySelector('#pw-code');
      const password = content.querySelector('#pw-new').value;
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Saving...';
      try {
        await setAccountPassword({
          password,
          current_password: currentEl ? currentEl.value : undefined,
          code: codeEl ? codeEl.value.trim() : undefined,
        });
        passwordFormOpen = false;
        showToast(user.has_password ? 'Password changed' : 'Password set', 'success');
        render(getUser());
      } catch (err) {
        errEl.textContent = err.message || 'Failed to save password';
        btn.disabled = false;
        btn.textContent = user.has_password ? 'Change password' : 'Set password';
      }
    });
  }

  // ── 2FA enrollment ──
  const enableBtn = content.querySelector('#enable-2fa-btn');
  if (enableBtn) {
    enableBtn.addEventListener('click', async () => {
      enableBtn.disabled = true;
      enableBtn.textContent = 'Starting...';
      try {
        setupData = await api.post('/auth/2fa/setup');
        render(user);
      } catch (err) {
        showToast(err.message || 'Failed to start 2FA setup', 'error');
        enableBtn.disabled = false;
        enableBtn.textContent = 'Enable 2FA';
      }
    });
  }

  const verifyForm = content.querySelector('#verify-2fa-form');
  if (verifyForm) {
    verifyForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = content.querySelector('#verify-2fa-btn');
      const errEl = content.querySelector('#verify-2fa-error');
      const code = content.querySelector('#verify-2fa-code').value.trim();
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Verifying...';
      try {
        oneTimeCodes = await verifyTwoFactorSetup(code);
        setupData = null;
        render(getUser());
      } catch (err) {
        errEl.textContent = err.status === 401
          ? 'Invalid or already-used code — wait for the next code.'
          : (err.message || 'Failed to verify code');
        btn.disabled = false;
        btn.textContent = 'Verify & Enable';
      }
    });
  }

  // ── Backup codes acknowledge / copy ──
  const ackBtn = content.querySelector('#ack-codes-btn');
  if (ackBtn) {
    ackBtn.addEventListener('click', () => {
      oneTimeCodes = null;
      showToast('Two-factor authentication enabled', 'success');
      render(getUser());
    });
  }
  const copyBtn = content.querySelector('#copy-codes-btn');
  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(oneTimeCodes.join('\n'));
        showToast('Backup codes copied', 'success');
      } catch {
        showToast('Copy failed — select and copy manually', 'error');
      }
    });
  }

  // ── Regenerate backup codes ──
  const openRegenBtn = content.querySelector('#open-regenerate-btn');
  if (openRegenBtn) {
    openRegenBtn.addEventListener('click', () => { regenerateFormOpen = true; disableFormOpen = false; render(user); });
  }
  const regenCancel = content.querySelector('#regenerate-cancel');
  if (regenCancel) {
    regenCancel.addEventListener('click', () => { regenerateFormOpen = false; render(user); });
  }
  const regenForm = content.querySelector('#regenerate-form');
  if (regenForm) {
    regenForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = content.querySelector('#regenerate-submit');
      const errEl = content.querySelector('#regenerate-error');
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Regenerating...';
      try {
        oneTimeCodes = await regenerateBackupCodes(readConfirmFields(content, 'regenerate'));
        regenerateFormOpen = false;
        render(getUser());
      } catch (err) {
        errEl.textContent = err.message || 'Failed to regenerate codes';
        btn.disabled = false;
        btn.textContent = 'Regenerate codes';
      }
    });
  }

  // ── Disable 2FA ──
  const openDisableBtn = content.querySelector('#open-disable-btn');
  if (openDisableBtn) {
    openDisableBtn.addEventListener('click', () => { disableFormOpen = true; regenerateFormOpen = false; render(user); });
  }
  const disableCancel = content.querySelector('#disable-cancel');
  if (disableCancel) {
    disableCancel.addEventListener('click', () => { disableFormOpen = false; render(user); });
  }
  const disableForm = content.querySelector('#disable-form');
  if (disableForm) {
    disableForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = content.querySelector('#disable-submit');
      const errEl = content.querySelector('#disable-error');
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Disabling...';
      try {
        await disableTwoFactor(readConfirmFields(content, 'disable'));
        disableFormOpen = false;
        showToast('Two-factor authentication disabled', 'success');
        render(getUser());
      } catch (err) {
        errEl.textContent = err.message || 'Failed to disable 2FA';
        btn.disabled = false;
        btn.textContent = 'Disable 2FA';
      }
    });
  }
}

export async function securitySettingsPage() {
  if (!(await checkAuth())) return;

  initLayout(getUser());
  updateLayoutContext({ projectId: null, projectName: null, activeView: null, viewLabel: 'Security' });

  setupData = null;
  oneTimeCodes = null;
  disableFormOpen = false;
  regenerateFormOpen = false;
  passwordFormOpen = false;
  // The cached currentUser may predate a 2FA/password change made elsewhere
  // (or in a prior tab) — re-check before showing state that drives a
  // destructive-adjacent action (enabling/disabling 2FA, setting a password).
  await verifySession();
  render(getUser());
}
